import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import { getStandardsAnalytics } from "../../services/analyticsService";
import { scoreHex, scoreTextClass } from "../../utils/helpers/scoreColors";
import { SkeletonCard } from "../../components/Skeleton/Skeleton";
import RubricGate, { isRubricGated } from "../../components/RubricGate/RubricGate";
import useOrgScope from "../../utils/hooks/useOrgScope";
import { standardScopeLabel } from "../../utils/helpers/standardScope";
import {
  DASHBOARD_PERMISSION_CODES,
  OBSERVATION_PERMISSION_CODES,
  hasPermission,
} from "../../utils/permissions";

const num = (v) => (v == null ? 0 : Number(v));

const borderTier = (pct) => {
  if (pct == null) return "border-l-slate-300";
  if (pct >= 80) return "border-l-emerald-400";
  if (pct >= 60) return "border-l-lime-400";
  if (pct >= 40) return "border-l-amber-400";
  if (pct >= 20) return "border-l-orange-400";
  return "border-l-rose-500";
};

export default function Standards() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const canView = hasPermission(user, DASHBOARD_PERMISSION_CODES.SCHOOL_VIEW);
  const canCreateObservation = hasPermission(
    user,
    OBSERVATION_PERMISSION_CODES.CREATE,
  );
  const { effectiveOrgUuid } = useOrgScope();

  const [rows, setRows] = useState([]);
  const [activeRubrics, setActiveRubrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [sortMode, setSortMode] = useState("rubric");

  useEffect(() => {
    if (!canView) return undefined;
    const controller = new AbortController();
    setIsLoading(true);
    setLoadError("");
    getStandardsAnalytics({
      params: { organization: effectiveOrgUuid },
      signal: controller.signal,
    })
      .then((data) => {
        if (controller.signal.aborted) return;
        setRows(data?.standards || []);
        setActiveRubrics(data?.active_rubrics || null);
        setIsLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === "ERR_CANCELED") return;
        setLoadError(t("standards.loadError"));
        setIsLoading(false);
      });
    return () => controller.abort();
  }, [canView, effectiveOrgUuid, t]);

  // Page-level facts. Every lesson scores the standards shared by all
  // subjects, so the busiest standard's count is the rubric's lesson count —
  // stated once here; a subject-scoped card states its own below.
  const stats = useMemo(() => {
    if (rows.length === 0) return null;
    const scored = rows.filter((r) => r.avg_percentage != null);
    const obsCount = Math.max(...rows.map((r) => r.observation_count || 0));
    const schoolAvg = scored.length
      ? scored.reduce((sum, r) => sum + num(r.avg_percentage), 0) / scored.length
      : null;
    const lowest = scored.length
      ? scored.reduce((min, r) =>
          num(r.avg_percentage) < num(min.avg_percentage) ? r : min
        )
      : null;
    return { obsCount, schoolAvg, lowest };
  }, [rows]);

  const sortedRows = useMemo(() => {
    if (sortMode !== "score") return rows;
    return [...rows].sort(
      (a, b) =>
        (a.avg_percentage == null ? Infinity : num(a.avg_percentage)) -
        (b.avg_percentage == null ? Infinity : num(b.avg_percentage))
    );
  }, [rows, sortMode]);

  const goToStandard = (uuid) => navigate(`/standards/${uuid}`);

  return (
    <div className="p-6 bg-slate-50 min-h-full w-full">
      <div className="max-w-7xl mx-auto">
        {/* The school picker lives in the app header now — see BaseLayout. */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonCard key={i} className="h-36" lines={3} />
            ))}
          </div>
        ) : loadError ? (
          <div className="py-20 text-center text-sm text-rose-600">
            {loadError}
          </div>
        ) : isRubricGated(activeRubrics) ? (
          <RubricGate activeRubrics={activeRubrics} className="mt-8" />
        ) : rows.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-sm text-slate-400">{t("standards.empty")}</p>
            {canCreateObservation && (
              <button
                type="button"
                onClick={() => navigate("/observation")}
                className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition"
              >
                {t("standards.startObservationCta")}
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <p className="text-sm text-slate-500">
                {stats.obsCount === 0
                  ? t("standards.obsNone")
                  : stats.obsCount === 1
                  ? t("standards.obsOne")
                  : t("standards.obsCount", { count: stats.obsCount })}
                {stats.schoolAvg != null && (
                  <>
                    {" · "}
                    {t("standards.contextAvg", {
                      pct: Math.round(stats.schoolAvg),
                    })}
                  </>
                )}
                {stats.lowest && (
                  <>
                    {" · "}
                    {t("standards.lowestLabel")}{" "}
                    <button
                      type="button"
                      onClick={() => goToStandard(stats.lowest.standard.uuid)}
                      className="font-semibold text-indigo-600 hover:underline"
                    >
                      S{stats.lowest.standard.number} —{" "}
                      {Math.round(num(stats.lowest.avg_percentage))}%
                    </button>
                  </>
                )}
              </p>
              <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
                {[
                  { v: "rubric", label: t("standards.sortRubric") },
                  { v: "score", label: t("standards.sortWorst") },
                ].map(({ v, label }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setSortMode(v)}
                    className={`px-3 py-1 rounded-md text-xs font-semibold whitespace-nowrap transition ${
                      sortMode === v
                        ? "bg-white shadow text-slate-900"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {sortedRows.map((row) => {
                const std = row.standard || {};
                const scopeLabel = standardScopeLabel(t, std);
                const pct = row.avg_percentage != null
                  ? Math.round(num(row.avg_percentage))
                  : null;
                const delta =
                  pct != null && stats.schoolAvg != null
                    ? Math.round(pct - stats.schoolAvg)
                    : null;
                return (
                  <div
                    key={std.uuid}
                    role="button"
                    tabIndex={0}
                    onClick={() => goToStandard(std.uuid)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        goToStandard(std.uuid);
                      }
                    }}
                    className={`group bg-white rounded-2xl border border-slate-200 border-l-4 ${borderTier(pct)} shadow-sm p-6 flex flex-col cursor-pointer transition hover:shadow-md hover:border-slate-300`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                        S{std.number}
                      </span>
                      {pct != null ? (
                        <span
                          className={`text-2xl font-black tabular-nums ${scoreTextClass(pct)}`}
                        >
                          {pct}%
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-slate-400">
                          {t("teacher.noData")}
                        </span>
                      )}
                    </div>
                    <h3
                      className="text-base font-bold text-slate-800 leading-snug line-clamp-2"
                      title={std.title}
                    >
                      {std.title}
                    </h3>
                    {scopeLabel && (
                      <p
                        className="mt-1.5 text-[11px] font-semibold text-indigo-700 truncate"
                        title={scopeLabel}
                      >
                        {scopeLabel} ·{" "}
                        {t("standards.lessonsCount", { count: row.observation_count || 0 })}
                      </p>
                    )}

                    {pct != null && (
                      <div className="mt-4 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: scoreHex(pct),
                          }}
                        />
                      </div>
                    )}

                    <div className="mt-auto pt-3 flex items-center justify-between gap-3">
                      {pct != null ? (
                        <>
                          <span
                            className={`text-xs font-semibold ${
                              delta != null && delta <= -5
                                ? "text-rose-600"
                                : "text-slate-500"
                            }`}
                          >
                            {t("standards.vsSchoolAvg", {
                              delta: `${delta > 0 ? "+" : ""}${delta}`,
                            })}
                          </span>
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 group-hover:text-indigo-600 transition-colors whitespace-nowrap">
                            {t("standards.viewBreakdown")}
                            <svg
                              className="w-3.5 h-3.5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M7.21 14.77a.75.75 0 01.02-1.06L11.17 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </span>
                        </>
                      ) : canCreateObservation ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/observation");
                          }}
                          className="text-xs font-semibold text-indigo-600 hover:underline text-left"
                        >
                          {t("standards.startObservation")}
                        </button>
                      ) : (
                        <span className="text-xs font-medium text-slate-400">
                          {t("teacher.noData")}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
